import { useCallback } from 'react'
import { usePlatform } from '@/lib/platform'
import { captureToolDeltaExcel } from '@/lib/snapshot/delta-capture-excel'
import { captureToolDeltaSheets } from '@/lib/snapshot/delta-capture-sheets'
import {
  discardDeltaChain,
  finalizeDeltaChain,
  startDeltaChain,
} from '@/lib/snapshot/delta-tool-wrapper'
import { resetDirtyTracker } from '@/lib/snapshot/dirty-tracker'
import {
  associateTurnWithAfterSnapshot,
  associateTurnWithSnapshot,
  saveSnapshot,
  useSnapshot,
} from '@/lib/snapshot/snapshot'

/**
 * Hook to handle snapshot capture, delta chain management, and turn association
 */
export function useSnapshotCapture() {
  const { captureSnapshot } = useSnapshot()
  const platform = usePlatform()

  /**
   * Capture a snapshot for a turn
   * Returns the snapshot ID, or null if capture failed
   */
  const captureSnapshotForTurn = useCallback(async (): Promise<
    string | null
  > => {
    let snapshotId: string | null = null
    try {
      const snapshot = await captureSnapshot()
      await saveSnapshot(snapshot)
      snapshotId = snapshot.id
      console.info(`Captured snapshot ${snapshotId} for turn`)
    } catch (error) {
      console.warn('Failed to capture snapshot', error)
      // Continue without snapshot - non-blocking
    }
    return snapshotId
  }, [captureSnapshot])

  /**
   * Associate a snapshot with a turn (before the turn)
   */
  const associateSnapshot = useCallback(
    async (
      turnId: string,
      chatId: string,
      snapshotId: string
    ): Promise<void> => {
      await associateTurnWithSnapshot(turnId, chatId, snapshotId)
    },
    []
  )

  /**
   * Associate an "after" snapshot with a turn (after the turn completes)
   */
  const associateAfterSnapshot = useCallback(
    async (
      turnId: string,
      chatId: string,
      snapshotId: string
    ): Promise<void> => {
      await associateTurnWithAfterSnapshot(turnId, chatId, snapshotId)
    },
    []
  )

  /**
   * Start delta capture chain for a new turn.
   * Call this after the "before" snapshot but before tools start executing.
   */
  const startDeltaCaptureForTurn = useCallback(
    (turnId: string, chatId: string): void => {
      // Reset dirty tracker for the new turn
      resetDirtyTracker()

      // Select the correct platform-specific delta capture function
      const captureFn =
        platform === 'excel' ? captureToolDeltaExcel : captureToolDeltaSheets

      startDeltaChain(turnId, chatId, captureFn)
    },
    [platform]
  )

  /**
   * Finalize the delta chain after all tools in a turn have executed.
   * Also captures the "after" full snapshot for fallback/comparison.
   */
  const finalizeTurnDeltas = useCallback(
    async (turnId: string, chatId: string): Promise<void> => {
      try {
        // Finalize and save the delta chain
        await finalizeDeltaChain()

        // Still capture "after" full snapshot (hybrid mode)
        // This provides fallback for incomplete delta chains and
        // supports the existing compare/revert UI
        const afterSnapshotId = await captureSnapshotForTurn()
        if (afterSnapshotId) {
          await associateAfterSnapshot(turnId, chatId, afterSnapshotId)
        }
      } catch (error) {
        console.warn('Failed to finalize turn deltas', error)
        // Non-blocking — existing snapshot system handles fallback
      }
    },
    [captureSnapshotForTurn, associateAfterSnapshot]
  )

  /**
   * Discard the current delta chain (e.g., on abort).
   */
  const discardTurnDeltas = useCallback((): void => {
    discardDeltaChain()
    resetDirtyTracker()
  }, [])

  return {
    captureSnapshotForTurn,
    associateSnapshot,
    associateAfterSnapshot,
    startDeltaCaptureForTurn,
    finalizeTurnDeltas,
    discardTurnDeltas,
  }
}
